import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth'd
  const [loading, setLoading] = useState(true);

  // Silently exchanges the refresh_token cookie for a new access_token cookie.
  // The access token is always short-lived (1 hour) — this is what keeps a
  // session alive past that without forcing a re-login, for as long as the
  // refresh token itself is valid (7 days normally, 30 with "remember me").
  const refreshAccessToken = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/auth/refresh`, { method: 'POST', credentials: 'include' });
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      let res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
      if (!res.ok && res.status === 401) {
        // Access token may have simply expired — try a silent refresh before giving up.
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        }
      }
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        setUser(false);
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [refreshAccessToken]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Proactively renew the access token every 45 minutes (before its 1-hour expiry) while
  // logged in, so an open tab never hits a 401 mid-session — only once the refresh token
  // itself expires does the user actually get signed out.
  useEffect(() => {
    if (!user) return;
    const renew = async () => {
      const ok = await refreshAccessToken();
      if (!ok) setUser(false);
    };
    const interval = setInterval(renew, 45 * 60 * 1000);
    // Also renew whenever the tab regains focus — covers a laptop sleeping through
    // the 45-minute interval and waking up mid-session.
    const onVisible = () => { if (document.visibilityState === 'visible') renew(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, refreshAccessToken]);

  const login = async (email, password, remember = false) => {
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, remember }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text || 'Unknown error' }; }
      if (!res.ok) {
        throw new Error(formatApiErrorDetail(data.detail));
      }
      setUser(data);
      return data;
    } catch (err) {
      if (err.message.includes('body stream')) {
        throw new Error('Invalid email or password');
      }
      throw err;
    }
  };

  const register = async (email, password, name, organization) => {
    try {
      const res = await fetch(`${API}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name, organization }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text || 'Unknown error' }; }
      if (!res.ok) {
        throw new Error(formatApiErrorDetail(data.detail));
      }
      setUser(data);
      return data;
    } catch (err) {
      if (err.message.includes('body stream')) {
        throw new Error('Registration failed. Please try again.');
      }
      throw err;
    }
  };

  const logout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
